import type { IScope } from "angular";

import "angular-translate";

import type { IEquipmentFunctionalityTypeSlim, IEquipmentType, IResponseWithLoading } from "App/Modules/Models";
import { EquipmentClass, EquipmentFunctionalityClass } from "App/Modules/Models";
import type { IEquipmentTypeRepository } from "App/Modules/Repositories";
import type { IModalService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services";

export default class EquipmentFunctionalitiesComponentController {
    public static $inject: string[] = ["$scope", "$translate", "mmEquipmentTypeRepository", "mmModalService"];

    public equipmentType: IResponseWithLoading<IEquipmentType>;

    public subCaption: string;

    public functionalityTypeDeleteResponse: {
        [id: string]: IResponseWithLoading<void>;
    };

    private tooltip: string;

    private functionalityTypeDeleteDisabled: boolean;

    constructor(
        private readonly $scope: IScope,
        private readonly $translate: ng.translate.ITranslateService,
        private readonly equipmentTypeRepository: IEquipmentTypeRepository,
        private readonly modalService: IModalService,
    ) {
        this.functionalityTypeDeleteResponse = {};
        this.tooltip = "";
    }

    public $onChanges() {
        if (this.equipmentType.data) {
            this.updateSubCaption(this.equipmentType.data.functionalityTypes.length);
        }
    }

    public addFunctionality(): void {
        this.modalService
            .open<IEquipmentFunctionalityTypeSlim, IEquipmentType>(
                "irAddEquipmentFunctionality",
                null,
                this.equipmentType.data,
            )
            .subscribe(
                (editResponse) => {
                    if (editResponse) {
                        this.equipmentType.data.functionalityTypes.push(editResponse);
                        this.updateSubCaption(this.equipmentType.data.functionalityTypes.length);
                    }
                },
                (reason) => reason,
            );
    }

    public deleteFunctionality(functionality: IEquipmentFunctionalityTypeSlim): void {
        extendWithLoadingAndFail(
            this.equipmentTypeRepository.deleteFunctionality(this.equipmentType.data.id, functionality.id),
        ).subscribe((response: IResponseWithLoading<void>) => {
            this.functionalityTypeDeleteResponse[functionality.id] = response;
            if (!response.isLoading) {
                this.equipmentType.data.functionalityTypes = this.equipmentType.data.functionalityTypes.filter(
                    (ft) => ft.id !== functionality.id,
                );
                this.updateSubCaption(this.equipmentType.data.functionalityTypes.length);
            }
        });
    }

    public isFunctionalityTypeDeleteDisabled(functionalityType: IEquipmentFunctionalityTypeSlim): boolean {
        const equipmentType: IEquipmentType = this.equipmentType.data;

        if (equipmentType) {
            const { functionalityTypes } = equipmentType;

            if (
                equipmentType.class === EquipmentClass.Computer &&
                functionalityType.class === EquipmentFunctionalityClass.Processing
            ) {
                this.tooltip = "Gui.Manage.DeleteProcessingFunctionalityTypeTooltip";

                return (
                    functionalityTypes.filter((type) => type.class === EquipmentFunctionalityClass.Processing)
                        .length === 1
                );
            }

            if (
                equipmentType.class === EquipmentClass.Camera &&
                functionalityType.class === EquipmentFunctionalityClass.Video
            ) {
                this.tooltip = "Gui.Manage.DeleteVideoFunctionalityTypeTooltip";

                return (
                    functionalityTypes.filter((type) => type.class === EquipmentFunctionalityClass.Video).length === 1
                );
            }
        }

        return false;
    }

    private updateSubCaption(functionalityCount: number): void {
        this.$translate(
            ["Entities.EquipmentFunctionalityType.FunctionalityCountFormat"],
            {
                functionalityCount,
            },
            "messageformat",
        ).then((translations) => {
            this.subCaption = translations["Entities.EquipmentFunctionalityType.FunctionalityCountFormat"];
        });
    }
}
